import { updateOrganization } from '@/api/organization.api.private';
import { useToast } from '@/components/ui/use-toast';
import { useState } from 'react';
import { useOrganizationContext } from '../organization.context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { camelCaseToWords } from 'shared/utils';
import { DEFAULT_WIDGET_ORDER, WIDGETS } from 'shared/constants';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Button } from '@/components/ui/button';
import { Save, RotateCcw } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import DraggableItem from '../../components/draggable-item';

/**
 * @param {{dashboardWidgetType: 'mobile' | 'web'}} param0
 */
export default function WidgetsBox({ dashboardWidgetType = 'mobile' }) {
  const { toast } = useToast();
  const { setOrganization, organization } = useOrganizationContext();

  const initialsWidgetOrder =
    organization.configuration?.dashboardWidgets?.[dashboardWidgetType] ?? DEFAULT_WIDGET_ORDER;

  // merge the existing widgets with all the widgets options
  const widgetSet = Array.from(new Set([...initialsWidgetOrder, ...Object.values(WIDGETS)]));

  const initialsWidget = widgetSet.map(w => ({
    id: w,
    checked: initialsWidgetOrder.includes(w),
  }));

  const [widgets, setWidgets] = useState(initialsWidget);
  const [loading, setLoading] = useState(false);

  // Save the new widget order
  const onSubmit = async () => {
    setLoading(true);

    const { data, error } = await updateOrganization({
      id: organization.id,
      data: {
        configuration: {
          dashboardWidgets: {
            [dashboardWidgetType]: widgets.filter(w => w.checked).map(w => w.id),
          },
        },
      },
    });

    if (error) {
      setLoading(false);
      return toast({
        title: 'Failed to update widget order',
        variant: 'destructive',
      });
    }

    setOrganization(oldOrganization => ({ ...oldOrganization, ...data }));
    setLoading(false);

    toast({
      title: 'Widget order has been updated',
      variant: 'success',
    });
  };

  // Function to move widget in the array
  const moveWidget = (dragIndex, hoverIndex) => {
    const draggedItem = widgets[dragIndex];
    const newWidgets = [...widgets];

    // Remove the item from its original position
    newWidgets.splice(dragIndex, 1);

    // Insert the item at the new position
    newWidgets.splice(hoverIndex, 0, draggedItem);

    setWidgets(newWidgets);
  };

  const toggleWidget = ({ id, checked }) => {
    const newWidgets = [...widgets];
    const widgetIndex = newWidgets.findIndex(w => w.id === id);

    if (checked) {
      // Remove the item from its original position
      newWidgets.splice(widgetIndex, 1);

      // push it to the end when unchecking
      newWidgets.push({ id, checked: false });
    } else {
      newWidgets[widgetIndex].checked = !checked;
    }

    setWidgets(newWidgets);
  };

  const resetWidgets = () => {
    setWidgets(initialsWidget);
  };

  // Check if any changes have been made (order or toggle state)
  const dirty = (() => {
    // Check if the length or items in the checked widgets array has changed
    const currentActiveWidgets = widgets.filter(w => w.checked).map(w => w.id);
    const initialActiveWidgets = initialsWidgetOrder;

    // Compare length
    if (currentActiveWidgets.length !== initialActiveWidgets.length) return true;

    // Compare each widget and its position
    for (let i = 0; i < currentActiveWidgets.length; i++) {
      if (currentActiveWidgets[i] !== initialActiveWidgets[i]) return true;
    }

    return false;
  })();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="capitalize">{dashboardWidgetType} Dashboard Widgets</CardTitle>
        <div className="flex gap-2 ">
          <Button size="sm" onClick={onSubmit} disabled={!dirty} loading={loading}>
            <Save className="w-4 h-4 mr-2" />
            Save
          </Button>
          <Button size="sm" onClick={resetWidgets} disabled={loading || !dirty}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <p className="text-sm text-gray-500 mb-4">
          Drag and drop widgets to reorder how they appear on the {dashboardWidgetType} dashboard.
        </p>
        <p className="text-sm text-gray-500 mb-4">
          Toggle on/off to show/hide the widget on the dashboard.
        </p>

        <DndProvider backend={HTML5Backend}>
          <div className="space-y-2">
            {widgets.map(({ id, checked }, index) => (
              <DraggableItem
                key={id}
                id={id}
                index={index}
                moveItem={moveWidget}
                dndGroup="WIDGETS">
                <div className="flex flex-col">
                  <h5 className="font-medium">{camelCaseToWords(id)}</h5>
                  <label className="text-xs text-gray-500">{id}</label>
                </div>

                <Switch
                  className="ml-auto"
                  checked={checked}
                  onCheckedChange={() => toggleWidget({ id, checked })}
                />
              </DraggableItem>
            ))}
          </div>
        </DndProvider>
      </CardContent>
    </Card>
  );
}
